/**
 * Hybrid storage node metrics calculator
 * Calculates all metrics specific to hybrid (S) nodes with flash + HDD
 */

import {
  getCompressionState,
  getJbodKey,
  calculateThroughput,
  calculateWriteEfficiency,
  calculateIops,
  resolveNetworkThroughput
} from '../shared/performanceHelpers';
import { calculateCapacity, calculateHybridBurstCapacity } from '../shared/capacityHelpers';
import { calculateTotalEffectiveCapacity } from '../shared/raw2Useable';
import { calculateSystemReliability } from '../shared/durabilityCalculator';

import { getProductPower, getProductWeight, getProductMaxPower } from '../../utils/configUtilities';
import { PART_NUMBERS } from '../../utils/partNumbers';
import type { HybridStorageBlock } from '../../domain';
import type { MetricsContext, ProductDb, MetricsDb } from '../types';

// Block-based replacement for partNumberUtils.generateHddPartNumber(config)
// (spec §3.1). Prefer the part number the UI stamped on the block — it decouples
// the SKU from the jbodSize × driveSize formula (e.g. 30 drives in a J78 chassis
// → VCH-5000-J78-480). Truthy check on purpose: '' falls through to the formula,
// matching the original exactly. Fallback formula serves legacy saved configs.
function jbodPartNumberForBlock(hybrid: HybridStorageBlock): string {
  if (hybrid.hddPartNumber) {
    return hybrid.hddPartNumber;
  }
  if (!hybrid.enabled || hybrid.hddCapacity === 0) {
    return `${PART_NUMBERS.STORAGE.HDD_BASE}${hybrid.jbodSize}-0`;
  }
  return `${PART_NUMBERS.STORAGE.HDD_BASE}${hybrid.jbodSize}-${hybrid.jbodSize * hybrid.hddCapacity}`;
}

/**
 * CONF-258: the hybrid throughput factors, separated from node count so the
 * metrics layer can expose a per-node rating alongside the system totals.
 * `hddRead`/`hddWrite` are the metricsDb per-node figures **before**
 * `mlecDerate` is applied — the calculator below multiplies them in.
 * `nvmeRead`/`nvmeWrite` are untouched by MLEC (the flash tier runs far above
 * any HDD spindle, CONF-93).
 * @param hybrid - Hybrid storage block from SystemConfiguration
 * @param ctx - Cross-box inputs (quarter, network)
 * @param productDb - Product database
 * @param metricsDb - Metrics database with performance data
 */
export function hybridThroughputFactors(
  hybrid: HybridStorageBlock,
  ctx: MetricsContext,
  productDb: ProductDb,
  metricsDb: MetricsDb,
) {
  const {
    jbodSize,
    encodingScheme: hybridEncodingScheme,
    compressionRatio: hybridCompressionRatio,
  } = hybrid;

  const quarter = ctx.quarter;
  const compressionState = getCompressionState(hybridCompressionRatio);
  const jbodKey = getJbodKey(jbodSize);
  const writeEfficiency = calculateWriteEfficiency(hybridEncodingScheme);

  // CONF-94: MLEC gives one HDD per VPOD to local raidz1 parity, so 8 of 9 spindles
  // carry data on a 4U108. The architect's model is "one drive less", applied to
  // both directions.
  //
  // No additional raidz1 write penalty is modelled. Partial-stripe
  // read-modify-write on HDD is real, but engineering has published no number for
  // it, and customers benchmark delivered performance against these figures — an
  // invented multiplier would be worse than a documented omission.
  //
  // hddPerVpod is derived here rather than imported: raw2Useable.js has its own in
  // a different module, and the two must not drift silently. If a third consumer
  // appears, lift it to one shared helper.
  const hddPerVpod = jbodSize / productDb.vpodsPerNode;
  const mlecDerate = hybrid.mlec ? (hddPerVpod - 1) / hddPerVpod : 1;

  // Get throughput from metricsDb based on JBOD and NVMe configuration
  // Throughput per node (vpod)
  const hddRead = metricsDb.hybrid[jbodKey][compressionState].readThroughputPerNode[quarter] || 0;
  const hddWrite = metricsDb.hybrid[jbodKey][compressionState].writeThroughputPerNode[quarter] || 0;
  // CONF-93: the NVMe (flash) tier follows min(card ceiling, protocol rate).
  // The JBOD tier above is untouched — HDDs run far below any of these wires.
  const { read: nvmeRead, write: nvmeWrite } =
    resolveNetworkThroughput(metricsDb, ctx.networkOption, ctx.networkProtocol, compressionState, quarter);

  return { nvmeRead, nvmeWrite, hddRead, hddWrite, writeEfficiency, mlecDerate };
}

/**
 * Calculate all hybrid node-specific metrics
 * @param hybrid - Hybrid storage block from SystemConfiguration
 * @param ctx - Cross-box inputs (quarter)
 * @param productDb - Product database
 * @param metricsDb - Metrics database with performance data
 * @returns Hybrid node metrics (flat, same keys as before)
 */
export default function calculateHybridNodeMetrics(
  hybrid: HybridStorageBlock,
  ctx: MetricsContext,
  productDb: ProductDb,
  metricsDb: MetricsDb,
) {
  const {
    nodeCount: hybridNodeCount,
    vpodSsdCapacity: hybridVpodSsdCapacity,
    hddCapacity: hybridHddCapacity,
    jbodSize,
    encodingScheme: hybridEncodingScheme,
    spares: hybridSpares,
    compressionRatio: hybridCompressionRatio
  } = hybrid;

  const ssdPerVpod = metricsDb.global.ssdPerVpod;

  // ========== CAPACITY CALCULATIONS ==========
  const totalHybridSsdCapacity = calculateCapacity(hybridNodeCount, ssdPerVpod, hybridVpodSsdCapacity);
  const totalHddCapacity = calculateCapacity(hybridNodeCount, jbodSize, hybridHddCapacity);
  const totalHybridRawCapacity = totalHybridSsdCapacity + totalHddCapacity;

  const hybridCapacityConfig = {
    nodeCount: hybridNodeCount,
    jbodSize: jbodSize,
    vpodHddCapacity: hybridHddCapacity,
    vpodSsdCapacity: hybridVpodSsdCapacity,
    veloCount: 0,
    veloSsdCapacity: 0,
    vpodsPerNode: productDb.vpodsPerNode,
    // CONF-94. Already clamped for eligibility at the metrics seam
    // (src/metrics/index.ts), so no guard is needed here.
    mlec: hybrid.mlec,
  };

  const hybridCapacityResults = calculateTotalEffectiveCapacity(
    hybridCapacityConfig,
    hybridEncodingScheme,
    hybridSpares
  );

  const hybridVpodUseableCapacity = hybridCapacityResults.vpodUseableCapacity;
  const hybridEffectiveCapacity = hybridCapacityResults.totalEffectiveCapacity;
  const hybridCompressedEffectiveCapacity = hybridEffectiveCapacity * hybridCompressionRatio;

  // This is NVMe Capacity available for burst operations in a hybrid system. This needs to change when compression changes.
  // MORK TODO : Bug in UI - this does not change when compression ratio changes. Investigate.
  // shared/capacityHelpers is not converted this subtask (spec §2.3) and reads
  // flat-named fields — hand it a minimal flat-shaped object built from the block.
  const hybridBurstCapacity = calculateHybridBurstCapacity(
    { hybridVpodSsdCapacity, hybridNodeCount },
    ssdPerVpod,
    hybridEncodingScheme
  );

  // ========== PERFORMANCE CALCULATIONS ==========
  // CONF-258: factors extracted to hybridThroughputFactors so the metrics
  // layer can also derive a per-node throughput basis from the same values.
  const f = hybridThroughputFactors(hybrid, ctx, productDb, metricsDb);
  const writeEfficiency = f.writeEfficiency;

  // Get throughput from metricsDb based on JBOD and NVMe configuration
  // Throughput per node (vpod)
  const hybridJbodReadTransferRatePerNode = f.hddRead * f.mlecDerate;
  const hybridJbodWriteTransferRatePerNode = f.hddWrite * f.mlecDerate;
  // CONF-93: the NVMe (flash) tier follows min(card ceiling, protocol rate).
  // The JBOD tier above is untouched — HDDs run far below any of these wires.
  const hybridNvmeReadTransferRatePerNode = f.nvmeRead;
  const hybridNvmeWriteTransferRatePerNode = f.nvmeWrite;

  // MORK TODO : write some test prints to check transfer rate per node against jbod78 (12.48) and jbod108 (17.28) old values

  // Total throughput across all hybrid nodes
  const hybridJbodReadTransferRate = calculateThroughput(hybridNodeCount, hybridJbodReadTransferRatePerNode);
  const hybridJbodWriteTransferRate = writeEfficiency * calculateThroughput(hybridNodeCount, hybridJbodWriteTransferRatePerNode);
  // MORK TODO : Need to investigate how burst rates are used in the UI and App - I currently believe they are used to display NVMe throughput on a hybrid system.
  const hybridNvmeReadTransferRate = calculateThroughput(hybridNodeCount, hybridNvmeReadTransferRatePerNode);
  const hybridNvmeWriteTransferRate = writeEfficiency * calculateThroughput(hybridNodeCount, hybridNvmeWriteTransferRatePerNode);

  // IOPS from metricsDb
  const hybridIops = calculateIops(hybridNodeCount, metricsDb.global.hybridIopsPerNode);


  // ========== RELIABILITY CALCULATIONS ==========
  // CONF-228 (CONF-213 F8): the vendor is passed so its own failure constants and drive sets per node
  // apply. Before this, every vendor ran on AIC's, which kept CONF-182's Dell correction
  // out of the product.
  const hybridReliabilityMetrics = calculateSystemReliability({
    hardwareVendor: ctx.hardwareVendor,
    jbodSize,
    nodeCount: hybridNodeCount,
    vpodHddCapacity: hybridHddCapacity,
    vpodSsdCapacity: hybridVpodSsdCapacity,
    encodingScheme: hybridEncodingScheme,
    // CONF-94. Already eligibility-clamped at the metrics seam.
    mlec: hybrid.mlec,
  });

  // MORK TODO : The hybrid Nines don't look quite accurate in the display. Investigate further.
  const hybridDurabilityNines = hybridReliabilityMetrics.durabilityNines;
  const hybridAvailabilityNines = hybridReliabilityMetrics.availabilityNines;

  // ========== PHYSICAL CALCULATIONS ==========

  // Determine storage node part number (dual actuator vs standard)
  const hybridStorageNodePartNumber = hybrid.hddCapacity === 18
    ? PART_NUMBERS.NODES.HYBRID_DUAL_ACTUATOR
    : PART_NUMBERS.NODES.HYBRID_STORAGE;

  const hybridVpodNominalPower = hybridNodeCount * getProductPower(productDb, hybridStorageNodePartNumber);
  const hybridVpodMaxPower = hybridNodeCount * getProductMaxPower(productDb, hybridStorageNodePartNumber);
  const hybridVpodWeight = hybridNodeCount * getProductWeight(productDb, hybridStorageNodePartNumber);
  // CONF-230: the Supermicro Mixed Fleet node is 2U, the others 1U.
  const hybridVpodRU = hybridNodeCount * ctx.rackHeights.hybrid;

  // JBOD physical specs (always present for hybrid)
  const jbodPartNumber = jbodPartNumberForBlock(hybrid); // MORK TODO : we are getting partnumber for HDD not JBODs. Need to refactor to JBOD.
  const jbodNominalPower = hybridNodeCount * getProductPower(productDb, jbodPartNumber);
  const jbodMaxPower = hybridNodeCount * getProductMaxPower(productDb, jbodPartNumber);
  const jbodWeight = hybridNodeCount * getProductWeight(productDb, jbodPartNumber);
  const jbodRU = hybridNodeCount * ctx.rackHeights.jbod;

  const hybridTotalNominalPower = hybridVpodNominalPower + jbodNominalPower;
  const hybridTotalMaxPower = hybridVpodMaxPower + jbodMaxPower;
  const hybridTotalWeight = hybridVpodWeight + jbodWeight;
  const hybridTotalRU = hybridVpodRU + jbodRU;

  // ========== RETURN ALL METRICS ==========
  return {
    // Capacity
    totalHybridSsdCapacity,
    totalHddCapacity,
    totalHybridRawCapacity,
    hybridVpodUseableCapacity,
    hybridEffectiveCapacity,
    hybridCompressedEffectiveCapacity,
    hybridBurstCapacity,

    // Performance
    hybridIops,
    hybridJbodReadTransferRate: hybridJbodReadTransferRate,
    hybridJbodWriteTransferRate: hybridJbodWriteTransferRate,
    hybridNvmeReadTransferRate: hybridNvmeReadTransferRate,
    hybridNvmeWriteTransferRate: hybridNvmeWriteTransferRate,

    // Reliability
    hybridDurabilityNines,
    hybridAvailabilityNines,

    // Physical
    hybridVpodNominalPower,
    hybridVpodMaxPower,
    jbodNominalPower,
    jbodMaxPower,
    hybridTotalNominalPower, // Part of system total
    hybridTotalMaxPower,     // Part of system total
    hybridVpodWeight,
    jbodWeight,
    hybridTotalWeight,      // Part of system total
    hybridVpodRU,
    jbodRU,
    hybridTotalRU           // Part of system total
  };
}
